if (!customElements.get('subscription-widget')) {
  class SubscriptionWidget extends HTMLElement {
    connectedCallback() {
      this.hiddenInputs = this.planFields();
      try {
        this.data = JSON.parse(this.querySelector('[data-cw-json]').textContent);
      } catch (e) {
        this.data = { plans: {}, variants: {} };
      }
      this.mode = this.dataset.mode || 'sub';
      this.planId = this.dataset.planId || '';
      this.variantId = this.dataset.variantId;
      this.servings = parseFloat(this.dataset.servings) || 0;
      this.currency = this.dataset.currency || 'USD';

      this.addEventListener('change', (e) => {
        if (e.target.classList.contains('cw-radio')) {
          this.mode = e.target.value;
        } else if (e.target.classList.contains('cw-freq__select')) {
          this.planId = e.target.value;
          this.mode = 'sub';
        } else {
          return;
        }
        this.update();
      });

      // Whole card is the hit area (price / ships / padding), not only the radio label
      this.addEventListener('click', (e) => {
        const card = e.target.closest('[data-cw-card]');
        if (!card || !this.contains(card)) return;
        if (e.target.closest('select, a, button, label, .cw-radio')) return;

        const radio = card.querySelector('.cw-radio');
        if (!radio || radio.checked) return;
        radio.checked = true;
        this.mode = radio.value;
        this.update();
      });

      // Keep in sync with the theme's variant picker
      this.onDocChange = (e) => {
        if (this.hiddenInputs.includes(e.target)) return;
        setTimeout(() => this.checkVariant(), 50);
      };
      document.addEventListener('change', this.onDocChange);
      this.timer = setInterval(() => this.checkVariant(), 700);

      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onDocChange);
      clearInterval(this.timer);
    }

    currentVariantId() {
      const form = document.getElementById(this.dataset.formId);
      const field = form && form.elements['id'];
      if (field && field.value) return String(field.value);
      return new URLSearchParams(location.search).get('variant') || this.variantId;
    }

    checkVariant() {
      const id = this.currentVariantId();
      if (!id || id === this.variantId) return;
      this.variantId = id;
      this.update();
    }

    money(cents, { trim = false, code = false } = {}) {
      const digits = trim && Math.round(cents) % 100 === 0 ? 0 : 2;
      const text = new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(cents / 100);
      return code ? `${text} ${this.currency}` : text;
    }

    setText(selector, text) {
      const el = this.querySelector(selector);
      if (el) el.textContent = text;
    }

    // Collect every selling_plan input this widget controls
    planFields() {
      const fields = [];
      const form = document.getElementById(this.dataset.formId);

      if (form) {
        let field = form.querySelector('[data-pdp-selling-plan]');
        if (!field) {
          field = document.createElement('input');
          field.type = 'hidden';
          field.name = 'selling_plan';
          field.setAttribute('data-pdp-selling-plan', '');
          form.appendChild(field);
        }
        fields.push(field);
      }

      // The widget's own hidden input
      const own = this.querySelector('[data-cw-selling-plan]');
      if (own && !fields.includes(own)) fields.push(own);

      return fields;
    }

    syncUrl(planId) {
      if (document.querySelectorAll('subscription-widget').length > 1) return;
      const url = new URL(window.location.href);
      const current = url.searchParams.get('selling_plan') || '';
      if (current === planId) return;
      if (planId) url.searchParams.set('selling_plan', planId);
      else url.searchParams.delete('selling_plan');
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    }

    update() {
      const v = this.data.variants[this.variantId];
      const subCard = this.querySelector('[data-cw-card="sub"]');
      const oneCard = this.querySelector('[data-cw-card="one"]');

      if (v) {
        // One-time card
        this.setText('[data-cw-one-price]', this.money(v.price));

        // Subscription card: keep the chosen plan if this variant has it
        const ids = Object.keys(v.plans);
        if (!v.plans[this.planId]) this.planId = ids[0] || '';
        if (subCard) subCard.hidden = ids.length === 0;
        if (ids.length === 0) this.mode = 'one';

        const select = this.querySelector('.cw-freq__select');
        if (select) {
          [...select.options].forEach((o) => {
            o.disabled = !v.plans[o.value];
          });
          select.value = this.planId;
        }

        const p = v.plans[this.planId];
        if (p) {
          const compare = v.price;
          const pct = compare > p.price ? Math.round(((compare - p.price) * 100) / compare) : 0;
          this.setText('[data-cw-sub-price]', this.money(p.price));
          const subCompare = this.querySelector('[data-cw-sub-compare]');
          if (subCompare) {
            subCompare.hidden = pct <= 0;
            subCompare.textContent = this.money(compare);
          }
          this.setText('[data-cw-pct]', pct);
          const badge = this.querySelector('[data-cw-badge]');
          if (badge) badge.hidden = pct <= 0;
          this.setText('[data-cw-sub-interval]', this.data.plans[this.planId] || '');
        }
      }

      this.querySelectorAll('[data-cw-benefits]').forEach((ul) => {
        ul.hidden = ul.dataset.cwBenefits !== String(this.planId);
      });

      if (!oneCard && this.mode === 'one') this.mode = 'sub';

      // One-time → empty + disabled; subscription → plan id + enabled
      const planId = this.mode === 'sub' ? String(this.planId || this.dataset.planId || '') : '';
      this.hiddenInputs.forEach((input) => {
        input.value = planId;
        if (planId === '') input.setAttribute('disabled', '');
        else input.removeAttribute('disabled');
      });

      this.syncUrl(planId);

      if (this.hiddenInputs[0]) {
        this.hiddenInputs[0].dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (oneCard) oneCard.classList.toggle('is-selected', this.mode === 'one');
      if (subCard) subCard.classList.toggle('is-selected', this.mode === 'sub');
      this.querySelectorAll('.cw-radio').forEach((r) => {
        r.checked = r.value === this.mode;
      });
    }
  }
  customElements.define('subscription-widget', SubscriptionWidget);
}